/**
 * BuildingProgressBar component - shows construction/upgrade progress with countdown.
 */

import { useState, useEffect } from 'react';
import { formatDuration } from '@/types/buildings';

interface BuildingProgressBarProps {
    /** ISO timestamp when upgrade started */
    startedAt: string;
    /** ISO timestamp when upgrade completes */
    completesAt: string;
    /** Callback when upgrade completes */
    onComplete?: () => void;
    /** Label to show (e.g., "Building...", "Upgrading Level...") */
    label?: string;
}

/**
 * Displays a progress bar with countdown timer for building upgrades.
 */
export function BuildingProgressBar({
    startedAt,
    completesAt,
    onComplete,
    label = 'In Progress...',
}: BuildingProgressBarProps) {
    const [progress, setProgress] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState('');

    useEffect(() => {
        const startTime = new Date(startedAt).getTime();
        const endTime = new Date(completesAt).getTime();
        const totalDuration = endTime - startTime;

        const updateProgress = () => {
            const now = Date.now();
            const elapsed = now - startTime;
            const remaining = endTime - now;

            if (remaining <= 0) {
                setProgress(100);
                setTimeRemaining('Complete!');
                onComplete?.();
                return;
            }

            const percent = Math.min(100, (elapsed / totalDuration) * 100);
            setProgress(percent);

            // Format remaining time
            const remainingMinutes = Math.ceil(remaining / 60000);
            setTimeRemaining(formatDuration(remainingMinutes));
        };

        // Update immediately
        updateProgress();

        // Update every second
        const interval = setInterval(updateProgress, 1000);

        return () => clearInterval(interval);
    }, [startedAt, completesAt, onComplete]);

    return (
        <div className="w-full">
            {/* Label and time remaining */}
            <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>{label}</span>
                <span>{timeRemaining}</span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                <div
                    className="bg-purple-500 h-full transition-all duration-1000 ease-linear"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );
}

export default BuildingProgressBar;

